/**
 * Theme Context Provider
 * Manages shared theme state and syncs Dark/Light mode
 * between Tailwind and MUI
 */

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeMode, ThemeContextValue } from '../types';
import {
  createAppTheme,
  parseThemeMode,
  syncTailwindDarkMode,
  THEME_MODE_STORAGE_KEY,
} from '../theme/theme';

// ============================================
// Theme Context
// ============================================

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// ============================================
// Custom Hook
// ============================================

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// ============================================
// Theme Provider Component
// ============================================

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Initialize theme from localStorage or system preference
  const [mode, setMode] = useState<ThemeMode>(() => {
    const stored = parseThemeMode(localStorage.getItem(THEME_MODE_STORAGE_KEY));
    if (stored) {
      return stored;
    }
    // Check system preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  // Create MUI theme based on current mode
  const muiTheme = useMemo(() => createAppTheme(mode), [mode]);

  // Sync Tailwind dark mode and persist to localStorage
  useEffect(() => {
    syncTailwindDarkMode(mode);
    localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
  }, [mode]);

  // Apply mode changes made in other tabs of this site
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== THEME_MODE_STORAGE_KEY) return;

      const nextMode = parseThemeMode(event.newValue);
      if (nextMode) {
        setMode(nextMode);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Toggle theme function
  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const contextValue: ThemeContextValue = {
    mode,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <StyledEngineProvider injectFirst>
        <MuiThemeProvider theme={muiTheme}>
          <CssBaseline />
          {children}
        </MuiThemeProvider>
      </StyledEngineProvider>
    </ThemeContext.Provider>
  );
};
